import { PaperclipIcon } from "lucide-react"
import {
    ArchiveBoxIcon,
    CommandLineIcon,
    CubeTransparentIcon,
    DocumentIcon,
    DocumentTextIcon,
    FilmIcon,
    MusicalNoteIcon,
    PhotoIcon,
    PresentationChartLineIcon,
    TableCellsIcon,
} from "@heroicons/react/24/outline"
import { hasDownloadLink } from "@/lib/fileLinks";

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

const FileFieldType = ({ value, accent = '#ea580c', accentHover = '#f97316' }) => {
    function humanFileSize(size) {
        // A file record without a size used to render "NaN undefined".
        const bytes = Number(size);
        if (!Number.isFinite(bytes) || bytes < 0) return 'Unknown size';
        const i = bytes === 0 ? 0 : Math.min(Math.floor(Math.log(bytes) / Math.log(1000)), UNITS.length - 1);
        return +((bytes / Math.pow(1000, i)).toFixed(2)) * 1 + ' ' + UNITS[i];
    }
    const getFileTypeForIcon = (fileName) => {
        const extension = String(fileName ?? "").split(".").pop()?.toLowerCase() || ""

        if (["jpg", "jpeg", "png", "gif", "svg", "webp", "ai", "psd"].includes(extension)) return "image"
        if (["mp4", "avi", "mov", "mkv", "webm"].includes(extension)) return "video"
        if (["ppt", "pptx"].includes(extension)) return "slides"
        if (["xls", "xlsx", "csv", "ods"].includes(extension)) return "excel"
        if (["exe", "bin", "dll", "dmg", "appimage"].includes(extension)) return "binary"
        if (["dwg", "dxf", "3ds"].includes(extension)) return "cad"
        if (["mp3", "wav", "flac", "aac", "ogg"].includes(extension)) return "audio"
        if (["zip", "rar", "7z", "tar", "gz", "bz2"].includes(extension)) return "archive"
        if (["doc", "docx", "txt", "rtf", "pdf"].includes(extension)) return "document"

        return "file"
    }
    const getFileIcon = (type) => {
        switch (type) {
            case "image":
                return <PhotoIcon aria-hidden="true" className="w-5 h-5 text-gray-500" strokeWidth={2} />
            case "video":
                return <FilmIcon aria-hidden="true" className="w-5 h-5 text-gray-500" strokeWidth={2} />
            case "slides":
                return <PresentationChartLineIcon aria-hidden="true" className="w-5 h-5 text-gray-500" strokeWidth={2} />
            case "excel":
                return <TableCellsIcon aria-hidden="true" className="w-5 h-5 text-gray-500" strokeWidth={2} />
            case "binary":
                return <CommandLineIcon aria-hidden="true" className="w-5 h-5 text-gray-500" strokeWidth={2} />
            case "cad":
                return <CubeTransparentIcon aria-hidden="true" className="w-5 h-5 text-gray-500" strokeWidth={2} />
            case "audio":
                return <MusicalNoteIcon aria-hidden="true" className="w-5 h-5 text-gray-500" strokeWidth={2} />
            case "archive":
                return <ArchiveBoxIcon aria-hidden="true" className="w-5 h-5 text-gray-500" strokeWidth={2} />
            case "document":
                return <DocumentTextIcon aria-hidden="true" className="w-5 h-5 text-gray-500" strokeWidth={2} />
            default:
                return <DocumentIcon aria-hidden="true" className="w-5 h-5 text-gray-500" strokeWidth={2} />
        }
    }
    if (!value) {
        return (
            <p className="w-full px-4 py-3 text-sm text-gray-500 border border-gray-200 border-dashed rounded-md">
                No file attached.
            </p>
        );
    }

    return (
        <ul role="list" className="w-full border border-gray-200 divide-y divide-gray-100 rounded-md">
            <li className="flex items-center justify-between py-4 pl-4 pr-5 text-sm/6">
                <div className="flex items-center flex-1 w-0">
                    {getFileIcon(getFileTypeForIcon(value.filename))}
                    {/* Name over size: side by side, a long name on a phone
                        wrapped a syllable to a line. */}
                    <div className="flex flex-col flex-1 min-w-0 ml-4 gap-0.5">
                        {/* Wraps instead of truncating: two files cut to the same
                            "EVP-750_…" on a phone could not be told apart. */}
                        <span title={value.filename} className="font-medium [overflow-wrap:anywhere]">{value.filename}</span>
                        {/* gray-400 on white is ~2.5:1 — below the 4.5:1 that
                            WCAG 1.4.3 asks of body text. gray-600 clears it. */}
                        <span className="text-gray-600 shrink-0">
                            {humanFileSize(value.size)}
                        </span>
                    </div>
                </div>
                {/* The anchor exists only with a link to point at; see hasDownloadLink. */}
                {hasDownloadLink(value) && (
                    <div className="ml-4 shrink-0">
                        {/* A bare "Download" is ambiguous out of context — a screen
                            reader's link list would show nothing but repeats of it.
                            Naming the file makes the purpose stand alone
                            (WCAG 2.4.4 Link Purpose). */}
                        {/* py-3 -my-3: a 44px tap target without a taller row. */}
                        <a href={value.link} className="inline-block py-3 -my-3 font-medium"
                            aria-label={`Download ${value.filename}`}
                            style={{
                                color: accent,
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = accentHover}
                            onMouseLeave={e => e.currentTarget.style.color = accent}
                        >
                            Download
                        </a>
                    </div>
                )}
            </li>
        </ul>
    );
}

export default FileFieldType;